import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { AppContext, UnityBridge } from "@Context/common/general/context";
import { AnalyticsEvents } from "@Enums/index";
import type { IncomingReplayEvent, IncomingScenarioRetryReplay } from "@Globals/types";
import { useAnalytics } from "@hooks/common/analytics/use-analytics";
import { getFirstDecisionScore, type ReplayDecision } from "@hooks/common/session/replay-decision-utils";
import { parseReplayData } from "@Shared/schemas/replays/replay-data.schema";
import type { ReplayData } from "@Shared/types/ReplayData";
import { buildScenarioRetryPayload, createScenarioRetryAttemptId } from "./scenario-retry.utils";
import { computeReplayDecisions } from "./use-session-replay.utils";
import { ReplayStreamActions } from "./use-session-replay-stream";
import type { SessionResultsPlayItem } from "./use-session-results.types";

const LAUNCH_TIMEOUT_MS = 15_000;
const PLAY_TIMEOUT_MS = 10 * 60 * 1000;

type RetryPhase = "launching" | "playing" | "evaluating";

interface RetryAttempt {
  id: string;
  resultId: number;
}

export interface ScenarioRetryResult {
  attemptId: string;
  replayData: ReplayData;
  decisions: ReplayDecision[];
}

export const useScenarioRetry = (sessionId: string | undefined, userId: string | null) => {
  const { state } = useContext(AppContext);
  const { track } = useAnalytics();
  const { gameplayEntered, scenarioRetryReplay, replayEvent } = state.app.events;
  const activeRef = useRef<RetryAttempt | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [results, setResults] = useState<Record<number, ScenarioRetryResult>>({});
  const [views, setViews] = useState<Record<number, boolean>>({});
  const [unityReadyReplays, setUnityReadyReplays] = useState<Record<string, number>>({});
  const [active, setActive] = useState<{ resultId: number; phase: RetryPhase } | null>(null);
  const [errorResultId, setErrorResultId] = useState<number | null>(null);

  const clearRetryTimeout = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  const finishAttempt = useCallback(
    (errorId: number | null = null) => {
      clearRetryTimeout();
      activeRef.current = null;
      setActive(null);
      setErrorResultId(errorId);
    },
    [clearRetryTimeout]
  );

  const scheduleTimeout = useCallback(
    (resultId: number, delay: number) => {
      clearRetryTimeout();
      timerRef.current = setTimeout(() => finishAttempt(resultId), delay);
    },
    [clearRetryTimeout, finishAttempt]
  );

  useEffect(() => {
    setResults({});
    setViews({});
    setUnityReadyReplays({});
    finishAttempt();
    let mounted = true;

    const onEntered = (event: { module?: string }) => {
      const attempt = activeRef.current;
      if (!attempt || (event.module && event.module !== "playcore")) return;
      setActive({ resultId: attempt.resultId, phase: "playing" });
      scheduleTimeout(attempt.resultId, PLAY_TIMEOUT_MS);
    };

    const onReplay = async (payload: IncomingScenarioRetryReplay) => {
      const attempt = activeRef.current;
      if (!attempt || payload.retry_attempt_id !== attempt.id || payload.original_cognitive_result_id !== attempt.resultId) return;
      if (payload.status !== "completed") {
        finishAttempt(payload.status === "failed" ? attempt.resultId : null);
        return;
      }

      const replayData = parseReplayData(payload.replay_data);
      if (!replayData || replayData.duration <= 0 || replayData.characters.length === 0 || !replayData.ball?.position.length) {
        finishAttempt(attempt.resultId);
        return;
      }

      // Claim the response before evaluating so duplicate bridge messages are ignored.
      activeRef.current = null;
      clearRetryTimeout();
      setActive({ resultId: attempt.resultId, phase: "evaluating" });
      const decisions = await computeReplayDecisions(replayData);
      if (!mounted) return;
      if (decisions === null) {
        finishAttempt(attempt.resultId);
        return;
      }

      setResults(previous => ({ ...previous, [attempt.resultId]: { attemptId: attempt.id, replayData, decisions } }));
      setViews(previous => ({ ...previous, [attempt.resultId]: true }));
      finishAttempt();
    };

    const onReplayReady = (event: IncomingReplayEvent) => {
      if (event.action !== ReplayStreamActions.REPLAY_READY) return;
      const entries = Array.isArray(event.data) ? event.data : event.data ? [event.data] : [event];
      for (const entry of entries) {
        const attemptId = entry.retry_attempt_id ?? event.retry_attempt_id;
        const resultId = entry.cognitive_result_id ?? event.cognitive_result_id;
        if (typeof attemptId !== "string" || !attemptId || resultId === undefined || !Number.isInteger(resultId) || resultId <= 0) continue;
        setUnityReadyReplays(previous => (previous[attemptId] === resultId ? previous : { ...previous, [attemptId]: resultId }));
      }
    };

    gameplayEntered.on(onEntered);
    scenarioRetryReplay.on(onReplay);
    replayEvent.on(onReplayReady);
    return () => {
      mounted = false;
      activeRef.current = null;
      clearRetryTimeout();
      gameplayEntered.remove(onEntered);
      scenarioRetryReplay.remove(onReplay);
      replayEvent.remove(onReplayReady);
    };
  }, [sessionId, userId, gameplayEntered, scenarioRetryReplay, replayEvent, clearRetryTimeout, finishAttempt, scheduleTimeout]);

  const launch = (playItem: SessionResultsPlayItem | undefined, originalDecisions: ReplayDecision[] = []) => {
    const payload = buildScenarioRetryPayload(playItem);
    const resultId = playItem?.cognitive_result_id;
    if (!payload || !resultId || active || activeRef.current) return;

    const attemptId = createScenarioRetryAttemptId();
    track(AnalyticsEvents.scenario_retry_clicked, {
      original_session_id: sessionId,
      original_cognitive_result_id: resultId,
      scenario_id: playItem.scenario_id,
      scenario_doc_id: playItem.scenario_doc_id,
      retry_attempt_id: attemptId,
      decision_score: getFirstDecisionScore(results[resultId]?.decisions ?? originalDecisions),
    });

    activeRef.current = { id: attemptId, resultId };
    setActive({ resultId, phase: "launching" });
    setErrorResultId(null);
    scheduleTimeout(resultId, LAUNCH_TIMEOUT_MS);
    UnityBridge.dispatchWebEvent({
      type: UnityBridge.OutgoingWebEvents.StartPlaylist,
      payload: { ...payload, retry_attempt_id: attemptId, original_cognitive_result_id: resultId, return_retry_replay: true },
    });
  };

  const setShowRetry = (resultId: number, show: boolean) => setViews(previous => ({ ...previous, [resultId]: show }));

  return {
    results,
    views,
    unityReadyReplays,
    activeResultId: active?.resultId ?? null,
    phase: active?.phase ?? null,
    errorResultId,
    launch,
    setShowRetry,
  };
};
